/** Localised number / currency / area formatters for Hive Homes. */

const aedFormatter = new Intl.NumberFormat("en-AE", {
  style: "currency",
  currency: "AED",
  maximumFractionDigits: 0,
});

const compactFormatter = new Intl.NumberFormat("en-AE", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function formatAED(price: number): string {
  return aedFormatter.format(price);
}

export function formatAEDCompact(price: number): string {
  return `AED ${compactFormatter.format(price)}`;
}

export function formatArea(sqft: number): string {
  return `${new Intl.NumberFormat("en-AE").format(sqft)} sq ft`;
}

export function formatBedrooms(beds: number): string {
  if (beds === 0) return "Studio";
  return `${beds} Bed${beds === 1 ? "" : "s"}`;
}

export function formatBathrooms(baths: number): string {
  if (baths <= 0) return "";
  return `${baths} Bath${baths === 1 ? "" : "s"}`;
}

export function formatPrice(price: number, period?: "yearly" | "monthly"): string {
  const value = formatAED(price);
  if (period === "yearly") return `${value}/yr`;
  if (period === "monthly") return `${value}/mo`;
  return value;
}

/**
 * Short, context-aware label that sits next to a price on a card or
 * detail page. The brief flagged "Asking" reading awkwardly above an
 * `AED 200,000/yr` rental, so we vary it:
 *
 *  - Off-plan / launches  → `From`     (entry-from price)
 *  - Long-term rentals    → `Rent`     (the price is the rent)
 *  - Resale / ready stock → `Asking`   (the seller's asking price)
 */
export function priceLabelFor(
  p: Pick<Property, "listingType" | "pricePeriod">,
): string {
  if (p.listingType === "off-plan") return "From";
  if (p.listingType === "rent" || p.pricePeriod) return "Rent";
  return "Asking";
}

/**
 * Property "presentation title" — used everywhere a card or detail page
 * needs a human-friendly headline.
 *
 * Reads as natural language rather than dot-joined segments (the client
 * disliked "Feature · Feature · Community Type"), e.g.:
 *
 *   "Studio Apartment in Azizi Star, Al Furjan"
 *   "4‑Bed Townhouse in Elan, Tilal Al Ghaf"
 */
import type { Property } from "@/types/property";

export function formatListingTitle(p: Property): string {
  const community = p.community?.trim() || "Dubai";
  const bedrooms = formatBedSummary(p.bedrooms, p.propertyType);
  const type = formatPropertyType(p.propertyType);
  const unit = bedrooms ? `${bedrooms} ${type}` : type;

  const sub = p.subCommunity?.trim();
  const place =
    sub && sub.toLowerCase() !== community.toLowerCase()
      ? `${toTitleCase(sub)}, ${community}`
      : community;

  return `${unit} in ${place}`;
}

function formatBedSummary(beds: number, type: Property["propertyType"]): string {
  // "Studio" only makes sense for apartment-like homes; offices, retail
  // units, and plots simply omit the bedroom segment.
  if (beds === 0) {
    return type === "apartment" || type === "duplex" || type === "penthouse"
      ? "Studio"
      : "";
  }
  // U+2011 NON-BREAKING HYPHEN keeps "4-Bed" together when the title
  // wraps across lines on small viewports / long titles.
  return `${beds}\u2011Bed`;
}

function formatPropertyType(t: Property["propertyType"]): string {
  switch (t) {
    case "apartment":
      return "Apartment";
    case "villa":
      return "Villa";
    case "townhouse":
      return "Townhouse";
    case "penthouse":
      return "Penthouse";
    case "duplex":
      return "Duplex";
    case "office":
      return "Office";
    case "retail":
      return "Retail Unit";
    case "plot":
      return "Plot";
    default:
      return "Home";
  }
}

function toTitleCase(input: string): string {
  return input
    .toLowerCase()
    .replace(/(^|[\s\-/])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase());
}
